// src/feature-module/support/GlobalSearch.tsx
//
// "I have an id — what is it, and whose?"
//
// The single most common thing a supporter is handed is an opaque identifier
// with no context: a cuid from a URL, a correlation id off an error, an invoice
// number read over the phone. Every other screen in this console needs the
// tenant first, which is backwards — the tenant is usually what they are trying
// to establish.
//
// One box, no type picker. The backend classifies the term, so the supporter
// does not have to know whether they are holding a case reference or a
// correlation id.

import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, Input, Tag, Empty, Spin, Alert, Table, Typography } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { useLazyGlobalSearchQuery } from "../../core/redux/services/adminSupportApi";

/** Colour by what kind of thing it is, so a scan of results reads at a glance. */
const KIND_COLOR: Record<string, string> = {
  tenant: "green", user: "blue", customer: "cyan", supplier: "geekblue",
  invoice: "gold", purchaseOrder: "orange", transaction: "purple", payment: "magenta",
  product: "lime", variant: "lime", prescription: "red", stockTransfer: "volcano",
  supportCase: "blue", impersonation: "orange", request: "purple",
};

const SHAPE_HELP: Record<string, string> = {
  cuid: "Looks like a record id — matched against ids across the platform.",
  uuid: "Looks like a correlation id — matched against request logs.",
  email: "Looks like an email — matched against users, customers and suppliers.",
  caseRef: "Looks like a support case reference.",
  text: "Free text — matched against names, references and document numbers.",
};

const GlobalSearch: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [term, setTerm] = useState(searchParams.get("q") ?? "");
  const [run, { data, isFetching }] = useLazyGlobalSearchQuery();

  const go = (v: string) => {
    const t = v.trim();
    if (t.length >= 3) run(t);
  };

  // Accept the term from the URL so the header's search box can hand off to
  // this page, and so a result set is a shareable link — the thing you want
  // when passing an investigation to a colleague.
  useEffect(() => {
    const q = (searchParams.get("q") ?? "").trim();
    if (q.length >= 3) { setTerm(q); run(q); }
  }, [searchParams, run]);

  // Keep the URL in step with what is actually on screen.
  const search = (v: string) => {
    const t = v.trim();
    setSearchParams(t ? { q: t } : {}, { replace: true });
    go(t);
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="mb-3">
          <h4 className="mb-1">Search</h4>
          <p className="text-muted mb-0">Find anything on the platform by id, reference, email or name</p>
        </div>

        <Card className="mb-3">
          <Input.Search
            autoFocus
            size="large"
            allowClear
            enterButton={<><SearchOutlined /> Search</>}
            placeholder="Paste an id, correlation id, invoice number, SUP- reference, email or name…"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            onSearch={search}
          />
          <p className="text-muted small mt-2 mb-0">
            No need to know the tenant, or what kind of thing it is — that is what this is for.
            Patient records are intentionally not searchable here.
          </p>
        </Card>

        {isFetching && <Card><Spin /> Searching…</Card>}

        {!isFetching && data && (
          <Card
            title={<span>{data.hits.length} result{data.hits.length === 1 ? "" : "s"} for <Typography.Text code>{data.term}</Typography.Text></span>}
          >
            {SHAPE_HELP[data.shape] && (
              <Alert className="mb-3" type="info" showIcon message={SHAPE_HELP[data.shape]} />
            )}
            {data.hits.length === 0 ? (
              <Empty description="Nothing on the platform matches that. If it came from a URL or an error, it may belong to a deleted record." />
            ) : (
              <Table
                rowKey={(r) => `${r.kind}:${r.id}`}
                size="small"
                pagination={false}
                dataSource={data.hits}
                onRow={(r) => ({
                  onClick: () => r.link && navigate(r.link),
                  style: { cursor: r.link ? "pointer" : "default" },
                })}
                columns={[
                  {
                    title: "Type", dataIndex: "kind", width: 150,
                    render: (v: string) => <Tag color={KIND_COLOR[v] ?? "default"}>{v}</Tag>,
                  },
                  { title: "Match", dataIndex: "label", ellipsis: true },
                  { title: "Detail", dataIndex: "sublabel", ellipsis: true, render: (v: any) => <span className="text-muted">{v ?? "—"}</span> },
                  {
                    title: "Tenant", dataIndex: "tenantName", width: 210,
                    render: (v: any) => (v ? <Tag color="green">{v}</Tag> : <span className="text-muted">—</span>),
                  },
                  {
                    title: "Id", dataIndex: "id", width: 150,
                    render: (v: string) => <Typography.Text copyable={{ text: v }} code style={{ fontSize: 11 }}>{v.slice(0, 10)}…</Typography.Text>,
                  },
                ]}
              />
            )}
          </Card>
        )}
      </div>
    </div>
  );
};

export default GlobalSearch;
